import { useCallback, useEffect, useRef, useState } from "react";
import { getWordJob } from "../../../services/Exams/ExamsService";
import { isLiveJob } from "./wordText";

const POLL_MS = 2000;
// The server fails a job it has not heard of for 10 minutes; past 20 the dialog stops asking ("taking too long").
const LIMIT_MS = 20 * 60 * 1000;
// Reads that may fail in a row (a network blip) before the dialog says it lost track of the job.
const MAX_FAILURES = 3;

/**
 * Follows an exam's Word job while the dialog is open: follow(job or uuid) shows a job the server returned (polled
 * every 2 s from the next tick while it is queued or running) or reads a job by its uuid first. Unmounting (the
 * dialog closed) stops it, the request under way aborted: the job goes on on the server, and GET word/ names it
 * again when the dialog opens.
 *
 * Returns {job, problem, follow, resume, clear}: `problem` is "poll_error" (reads failed in a row), "stalled" (still
 * under way after 20 minutes) or "gone" (404: no such job, or not this user's); resume() reads the same job again.
 */
export default function useWordJob() {
  const [job, setJob] = useState(null);
  const [problem, setProblem] = useState(null);
  // The job polled now ({uuid, started, failures}); a read answering for another one is dropped.
  const followRef = useRef(null);
  const timerRef = useRef(null);
  const abortRef = useRef(null);
  const aliveRef = useRef(false);
  const jobRef = useRef(job);
  jobRef.current = job;

  const stop = useCallback(() => {
    clearTimeout(timerRef.current);
    abortRef.current?.abort();
    followRef.current = null;
  }, []);

  const poll = useCallback(async (state) => {
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const data = await getWordJob(state.uuid, { signal: controller.signal });
      if (followRef.current !== state) return;
      state.failures = 0;
      setJob(data);
      if (!isLiveJob(data)) {
        followRef.current = null;
        return;
      }
      if (Date.now() - state.started >= LIMIT_MS) {
        followRef.current = null;
        setProblem("stalled");
        return;
      }
    } catch (err) {
      if (controller.signal.aborted || followRef.current !== state) return;
      if (err?.response?.status === 404) {
        followRef.current = null;
        setProblem("gone");
        return;
      }
      state.failures += 1;
      if (state.failures >= MAX_FAILURES) {
        followRef.current = null;
        setProblem("poll_error");
        return;
      }
    }
    timerRef.current = setTimeout(() => poll(state), POLL_MS);
  }, []);

  const follow = useCallback(
    (target) => {
      stop();
      if (!aliveRef.current) return;
      setProblem(null);
      const given = target && typeof target === "object" ? target : null;
      const uuid = given ? given.uuid : target;
      if (given) setJob(given);
      if (!uuid || (given && !isLiveJob(given))) return;
      const state = { uuid, started: Date.now(), failures: 0 };
      followRef.current = state;
      if (given) timerRef.current = setTimeout(() => poll(state), POLL_MS);
      else poll(state);
    },
    [poll, stop]
  );

  const resume = useCallback(() => {
    if (jobRef.current?.uuid) follow(jobRef.current.uuid);
  }, [follow]);

  // Forgets the job (a new Word is asked for, or the one followed ended and is no longer this dialog's).
  const clear = useCallback(() => {
    stop();
    setJob(null);
    setProblem(null);
  }, [stop]);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      stop();
    };
  }, [stop]);

  return { job, problem, follow, resume, clear };
}
